import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, Download, ExternalLink, CheckCircle2, Code, Rocket, DollarSign, ScrollText } from 'lucide-react';
import { Card } from '../components/ui/card';

const steps = [
  {
    n: 1,
    title: 'PWA ପ୍ରସ୍ତୁତ (Already Done ✓)',
    titleEn: 'PWA Ready',
    body: 'Gurusevakendra ପୂର୍ବରୁ PWA ଭାବରେ ପ୍ରସ୍ତୁତ ଅଛି — manifest.json, service worker, standalone display mode। ଏହା Play Store ପାଇଁ ପ୍ରଥମ ପଦକ୍ଷେପ।',
    icon: CheckCircle2, color: 'text-emerald-600',
  },
  {
    n: 2,
    title: 'PWABuilder ଖୋଲନ୍ତୁ',
    titleEn: 'Use PWABuilder',
    body: 'https://www.pwabuilder.com ଖୋଲି ଆପଣଙ୍କ site URL ପେଷ୍ଟ କରନ୍ତୁ। ଏହା ଏକ ମାଗଣା Microsoft tool ଯାହା PWA କୁ Android APK/AAB ରେ package କରେ।',
    link: 'https://www.pwabuilder.com/',
    icon: Code, color: 'text-blue-600',
  },
  {
    n: 3,
    title: 'Android Package ତିଆରି କରନ୍ତୁ',
    titleEn: 'Generate Android AAB',
    body: 'PWABuilder ରେ "Package for stores" → Android ଚୟନ କରନ୍ତୁ। Options: TWA (Trusted Web Activity)। App name: Gurusevakendra, Package ID: in.gurusevakendra.app। Download .aab file.',
    icon: Smartphone, color: 'text-purple-600',
  },
  {
    n: 4,
    title: 'Google Play Console ପାଇଁ Sign Up',
    titleEn: 'Play Console Account',
    body: '$25 (one-time) ପେମେଣ୍ଟ କରି Google Play Developer Account କରନ୍ତୁ (play.google.com/console)। KYC ପାଇଁ PAN ଓ address proof ଆବଶ୍ୟକ।',
    link: 'https://play.google.com/console/signup',
    icon: DollarSign, color: 'text-amber-600',
  },
  {
    n: 5,
    title: 'App Listing ପ୍ରସ୍ତୁତ',
    titleEn: 'Create App Listing',
    body: 'Play Console ରେ "Create App" → App ନାମ "Gurusevakendra", Category: Education। Screenshots (2-8), Feature graphic (1024×500), App icon (512×512), Privacy Policy URL ପ୍ରସ୍ତୁତ।',
    icon: ScrollText, color: 'text-rose-600',
  },
  {
    n: 6,
    title: 'AAB Upload ଓ Review',
    titleEn: 'Upload & Publish',
    body: 'Production track ରେ .aab upload କରନ୍ତୁ। Content rating fill-up କରନ୍ତୁ। Data Safety declare କରନ୍ତୁ। Google review ୭ ଦିନ ପର୍ଯ୍ୟନ୍ତ ନେଇପାରେ। Approval ପରେ live!',
    icon: Rocket, color: 'text-emerald-600',
  },
];

export default function PublishGuidePage() {
  return (
    <div className="px-4 pt-4 space-y-4">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
        <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #0891b2 0%, #6366f1 60%, #7c3aed 100%)' }}>
        <Smartphone size={26} />
        <h1 className="text-xl font-extrabold mt-2">Play Store ରେ Publish କରନ୍ତୁ</h1>
        <p className="text-xs text-white/90 mt-1">Step-by-step Guide · ୬ ଟି ଛୋଟ ପଦକ୍ଷେପ</p>
      </div>

      <Card className="rounded-2xl p-3 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-2">
          <div className="text-lg">💡</div>
          <p className="text-[11px] text-amber-800 leading-relaxed">
            <strong>ସୂଚନା:</strong> Google Play Console ପାଇଁ ଏକ-ଥର $25 (~₹2,100) ଫି ଦେବାକୁ ପଡ଼ିବ। APK/AAB build କରିବା ମାଗଣା। ଆପଣ Windows/Mac କୌଣସି computer ରୁ କରିପାରିବେ।
          </p>
        </div>
      </Card>

      <div className="space-y-3">
        {steps.map((s) => (
          <Card key={s.n} className="rounded-2xl p-4 border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center font-extrabold ${s.color}`}>{s.n}</div>
                <s.icon size={16} className={`mt-2 ${s.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-gray-900">{s.title}</div>
                <div className="text-[11px] text-emerald-600 font-semibold">{s.titleEn}</div>
                <p className="text-xs text-gray-700 mt-1.5 leading-relaxed">{s.body}</p>
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                    <ExternalLink size={12} /> ଖୋଲନ୍ତୁ
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Required Assets */}
      <Card className="rounded-2xl p-4 border-gray-200">
        <div className="font-bold text-gray-900 mb-2">📦 Required Assets Checklist</div>
        <div className="space-y-1.5 text-xs text-gray-700">
          {[
            'App Icon 512×512 PNG',
            'Feature Graphic 1024×500 PNG',
            '2-8 Phone Screenshots (min 1080×1920)',
            'Short description (max 80 chars)',
            'Full description (max 4000 chars)',
            'Privacy Policy URL (ଏହା Gurusevakendra ରେ ଅଛି)',
            'Content Rating certificate',
            'Data Safety declaration',
          ].map((it) => (
            <div key={it} className="flex items-center gap-2">
              <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
              <span>{it}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Alternate quick path */}
      <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50">
        <div className="font-bold text-emerald-800 mb-1">🚀 Quick Path: PWABuilder Bubblewrap</div>
        <p className="text-[11px] text-emerald-700 leading-relaxed">
          ଯଦି ଆପଣ CLI comfortable, terminal ରେ ଚଳାନ୍ତୁ:<br/>
          <code className="bg-white px-1 rounded font-mono text-[10px]">{"npx @bubblewrap/cli init --manifest=<your-site-url>/manifest.json"}</code>
        </p>
      </Card>

      {/* Quick links to assets & privacy */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/assets" data-testid="link-assets" className="block">
          <Card className="rounded-2xl p-4 border-emerald-200 bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-center hover:opacity-90">
            <div className="text-2xl">🎨</div>
            <div className="font-bold text-sm mt-1">Assets Download</div>
            <div className="text-[10px] text-emerald-50 mt-0.5">Icon + Feature Graphic PNG</div>
          </Card>
        </Link>
        <Link to="/privacy" data-testid="link-privacy" className="block">
          <Card className="rounded-2xl p-4 border-blue-200 bg-gradient-to-br from-blue-500 to-cyan-600 text-white text-center hover:opacity-90">
            <div className="text-2xl">🛡️</div>
            <div className="font-bold text-sm mt-1">Privacy Policy</div>
            <div className="text-[10px] text-blue-50 mt-0.5">Play Store ପାଇଁ ଆବଶ୍ୟକ URL</div>
          </Card>
        </Link>
      </div>

      <div className="text-center text-[10px] text-gray-500 pt-2 pb-4">
        କୌଣସି ପ୍ରଶ୍ନ ଥିଲେ WhatsApp କରନ୍ତୁ (+91 73258 93950) · Gurusevakendra Team
      </div>
    </div>
  );
}
